import React, { Component } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames/bind";
import FocusLock, { InFocusGuard } from "react-focus-lock";
import clientConfig from "@template/clientConfig";
import { SMALL_VIEWPORT } from "@template/state/modules/layout/constants";
import globalEventHandler, { events, keys } from "@template/globalEventHandler";
import withTranslation from "@template/components/translation";
import SearchIcon from "@template/components/SearchIcon";
import IconButton from "@template/components/IconButton";
import SearchDropdown from "@template/components/SearchDropdown";

import SearchControls from "./SearchControls";
import styles from "./SearchForm.css";

const cx = classNames.bind(styles);

const SEARCH_SUGGESTIONS_DEBOUNCE_INTERVAL = 300;
const MAX_CHARACTER_VALUE_FOR_SEARCH = "150";

class SearchForm extends Component {
  constructor(props) {
    super(props);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.renderSearchNode = this.renderSearchNode.bind(this);
    this.state = { unformattedTerm: "" };
  }
  /* istanbul ignore next */
  componentWillReceiveProps(nextProps) {
    const { isVisible } = this.props;
    const blockScrollStyle = "chrome-hide-overflow";

    if (!isVisible && nextProps.isVisible) {
      typeof document !== "undefined" &&
        document.body.classList.add(blockScrollStyle);
      setTimeout(() => this.termInputRef.focus(), 0);
    } else if (isVisible && !nextProps.isVisible) {
      typeof document !== "undefined" &&
        document.body.classList.remove(blockScrollStyle);
    }
  }

  componentDidMount() {
    this.closeSearchOverlay = globalEventHandler.addListener(
      events.closeSearchOverlay,
      this.handleBlur
    );

    this.globalKeyDownListener = globalEventHandler.addListener(
      events.keyDown,
      event => {
        if (event.key === keys.escape) {
          this.props.isVisible && this.handleClose(event.originalEvent);
        }
      }
    );

    this.setSearchTermIfPresentOnMount();
  }

  componentWillUnmount() {
    this.closeSearchOverlay && this.closeSearchOverlay.remove();
    this.globalKeyDownListener && this.globalKeyDownListener.remove();
  }

  setSearchTermIfPresentOnMount() {
    const { term, setSearchTerm } = this.props;

    if (term.trim().length > 0) {
      setSearchTerm(term);
    }
  }

  changeTerm(term) {
    const { setSearchTerm, loadSuggestions } = this.props;

    setSearchTerm(term);
    this.setState(() => ({
      unformattedTerm: term
    }));

    clearTimeout(this.timer);
    this.timer = setTimeout(
      loadSuggestions,
      SEARCH_SUGGESTIONS_DEBOUNCE_INTERVAL
    );
  }

  handleChange = ({ target }) => {
    const { isVisible, openSearch, viewport } = this.props;

    if (!isVisible) {
      openSearch({
        isModal: viewport === SMALL_VIEWPORT
      });
    }

    this.changeTerm(target.value);
  };

  handleClose = () => {
    const {
      viewport,
      closeSearch,
      setSearchTerm,
      focusLastActiveElement,
      lastActiveElements
    } = this.props;

    closeSearch({
      isModal: viewport === SMALL_VIEWPORT
    });

    setSearchTerm("");

    focusLastActiveElement(lastActiveElements);

    this.setState(() => ({
      unformattedTerm: ""
    }));
  };

  handleSubmit = event => {
    const { onSearch, term } = this.props;

    if (term.length === 0) {
      event.preventDefault();
    } else {
      this.setState(() => ({ unformattedTerm: term }));

      onSearch(term);
    }
  };

  handleFocus = () => {
    const { openSearch, viewport } = this.props;

    if (viewport !== SMALL_VIEWPORT) {
      openSearch({ isModal: false });
    }
  };

  handleKeyDown = e => {
    if (e.keyCode === 40) {
      //Down arrow
      this.listRef.firstElementChild.firstElementChild.focus();
    }
    if (e.shiftKey && e.keyCode == 9) {
      // shift tab on the search input
      // (no focus lock but needs to close the search form)
      this.handleBlur();
    }
  };

  handleBlur = () => {
    const { closeSearch, viewport } = this.props;

    if (viewport !== SMALL_VIEWPORT) {
      closeSearch({ isModal: false });
    }
  };

  handleSearchInputBlur = (p, e, ignoreTerm) => {
    const { isSearchResultsPanelOpen, term } = this.props;
    if (!isSearchResultsPanelOpen && (ignoreTerm || !term)) {
      this.handleBlur();
    }
  };

  handleSearchButtonBlur = () => {
    this.handleSearchInputBlur(null, null, true);
  };

  focusSelf = () => {
    setTimeout(() => this.termInputRef.focus(), 0);
  };

  clearSearchTerm = () => {
    this.changeTerm("");
    this.focusSelf();
  };

  renderSearchNode() {
    const {
      isVisible,
      hasVisibleResults,
      term,
      formatTranslation
    } = this.props;

    const buttonClassName = cx(styles.button, {
      [styles.button__active]: term
    });

    const resultsClassName = cx(styles.results, {
      [styles.results__active]: hasVisibleResults
    });

    const containerClassName = cx(styles.container, {
      [styles.container__active]: isVisible
    });

    const backdropClassName = cx(styles.backdrop, {
      [styles.backdrop__active]: isVisible
    });

    const fieldClassName = cx({
      [styles.field]: true,
      [styles.field__active]: isVisible
    });

    const searchAction = this.props.searchUrl;

    return (
      <div
        className={styles.searchContainer}
        onTouchMove={event => event.preventDefault()}
      >
        <div
          className={backdropClassName}
          aria-hidden="true"
          onClick={this.handleBlur}
          data-testid="search-overlay-shadow"
        />
        <form
          action={searchAction}
          method="get"
          onSubmit={this.handleSubmit}
          className={containerClassName}
          data-testid="search-form"
        >
          <div className={styles.fieldContainer} data-testid="search-field">
            <label htmlFor="chrome-search">
              <span className={styles.label}>
                {formatTranslation("searchbar_label")}
              </span>
              <input
                id="chrome-search"
                name="q"
                role="combobox"
                className={fieldClassName}
                type="search"
                autoComplete="off"
                placeholder={formatTranslation("searchbar_placeholder")}
                onChange={this.handleChange}
                onFocus={this.handleFocus}
                onBlur={this.handleSearchInputBlur}
                data-testid="search-input"
                ref={/* istanbul ignore next */ c => (this.termInputRef = c)}
                aria-autocomplete="both"
                aria-controls="search-results"
                aria-expanded={hasVisibleResults}
                maxLength={MAX_CHARACTER_VALUE_FOR_SEARCH}
                onKeyDown={this.handleKeyDown}
                value={this.state.unformattedTerm}
              />

              {term && (
                <IconButton
                  className={styles.field__clear}
                  icon={styles.close}
                  onClick={this.clearSearchTerm}
                  aria-label={formatTranslation(
                    "accessibility_search_clear_text_button_description"
                  )}
                  data-testid="search-clear-button"
                />
              )}

              <span
                className={styles.floatingLabel}
                aria-hidden={!term ? true : null}
              >
                {term && `${formatTranslation("icon_search")}:`}
              </span>
            </label>
            <button
              className={buttonClassName}
              type="submit"
              onBlur={this.handleSearchButtonBlur}
              data-testid="search-button-inline"
              disabled={term.length === 0}
            >
              <SearchIcon />
            </button>
            <section className={resultsClassName}>
              <SearchDropdown
                focusSearchBox={this.focusSelf}
                listRef={/* istanbul ignore next */ el => (this.listRef = el)}
              />
            </section>
          </div>
          <SearchControls viewable={[SMALL_VIEWPORT]}>
            <IconButton
              height={50}
              icon={styles.close}
              onClick={this.handleClose}
              aria-label={formatTranslation("icon_close")}
            />
          </SearchControls>
        </form>
      </div>
    );
  }

  render() {
    const { viewport, isVisible } = this.props;

    return (
      (!clientConfig.isServer() &&
        isVisible &&
        viewport === SMALL_VIEWPORT &&
        ReactDOM.createPortal(
          <div aria-label="Search" role="dialog" aria-modal="true">
            <FocusLock noFocusGuards={true}>
              <InFocusGuard>{this.renderSearchNode()}</InFocusGuard>
            </FocusLock>
          </div>,
          document.getElementById("chrome-modal-container")
        )) ||
      this.renderSearchNode()
    );
  }
}

SearchForm.propTypes = {
  closeSearch: PropTypes.func.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  hasVisibleResults: PropTypes.bool,
  isSearchResultsPanelOpen: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool,
  loadSuggestions: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  openSearch: PropTypes.func.isRequired,
  searchUrl: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  term: PropTypes.string.isRequired,
  viewport: PropTypes.string.isRequired,
  lastActiveElements: PropTypes.arrayOf(PropTypes.string),
  focusLastActiveElement: PropTypes.func
};

SearchForm.defaultProps = {
  isVisible: false,
  hasVisibleResults: false
};

export default withTranslation(SearchForm);
